import {Props_ApplicationProcess} from './ApplicationProcess.props'
import {CompileMarkdown} from '@ClientBase/React/ReactSnippetMDCompiler'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y } from 'swiper/modules'
import { SwiperNavigation, SwiperPagination, swiperPaginationOptions } from '../Shared/SwiperControls/SwiperControls'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

const ApplicationProcess = (props: Props_ApplicationProcess) => {
    const {heading, repeatable_steps, text} = props
    const [shouldUseSwiper, setShouldUseSwiper] = useState(false)
    const carouselId = "application-process-carousel"

    useEffect(() => {
        const checkSwiperCondition = () => {
            const screenWidth = window.innerWidth
            const hasMoreThan5Items = repeatable_steps && repeatable_steps.length > 5
            setShouldUseSwiper(screenWidth < 990 || hasMoreThan5Items)
        }

        checkSwiperCondition()
        window.addEventListener('resize', checkSwiperCondition)

        return () => window.removeEventListener('resize', checkSwiperCondition)
    }, [repeatable_steps])

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1] as const
            }
        }
    }

    return (
        <section className='application-process'>
            <div className={`application-process__container`}>
                {heading?.content?.value &&
                    <h2 className={`application-process__heading`}>
                        {heading.content.value}
                    </h2>
                }
                {repeatable_steps?.length > 0 && (
                    <>
                        {shouldUseSwiper ? (
                            <div className="application-process__swiper-container">
                                <SwiperNavigation uniqueId={carouselId} className="application-process__navigation" />
                                <Swiper
                                    className="application-process__steps"
                                    modules={[Navigation, Pagination, A11y]}
                                    a11y={{
                                        prevSlideMessage: `{{js.swiper.carousel.navigation.button.previous.aria-label}}`,
                                        nextSlideMessage: `{{js.swiper.carousel.navigation.button.next.aria-label}}`,
                                    }}
                                    navigation={{
                                        prevEl: `.swiper-navigation__button-prev-${carouselId}`,
                                        nextEl: `.swiper-navigation__button-next-${carouselId}`,
                                    }}
                                    pagination={{
                                        ...swiperPaginationOptions,
                                        el: `.swiper-pagination-${carouselId}`,
                                    }}
                                    slidesPerView={1}
                                    spaceBetween={20}
                                    breakpoints={{
                                        480: {
                                            slidesPerView: 2,
                                        },
                                        768: {
                                            slidesPerView: 3,
                                        },
                                    }}
                                >
                                    {repeatable_steps.map((step, index) => (
                                        <SwiperSlide key={index} className={`application-process__step`}>
                                            <a className={`application-process__link`} aria-label={step.step_link_aria_label?.value ? step.step_link_aria_label.value : 'application step'} href={step.step_link_target?.value}>
                                                <motion.div
                                                    className={`application-process__step-inner`}
                                                    initial={{ opacity: 0 }}
                                                    whileInView={{ opacity: 1 }}
                                                    viewport={{ once: true, amount: 0.3 }}
                                                    transition={{
                                                        duration: 0.5,
                                                        delay: index * 0.2,
                                                        ease: [0.4, 0, 0.2, 1] as const
                                                    }}
                                                >
                                                    <div className={`application-process__step-count`}>
                                                        {index + 1}
                                                    </div>
                                                    {step.step_heading?.value &&
                                                        <h3 className={`application-process__step-heading`}>
                                                            {step.step_heading.value}
                                                        </h3>
                                                    }
                                                </motion.div>
                                            </a>
                                            <div className={`application-process__step-decorator`}></div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <SwiperPagination uniqueId={carouselId} className="application-process__pagination" />
                            </div>
                        ) : (
                            <motion.div
                                className={`application-process__steps`}
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                            >
                                {repeatable_steps.map((step, index) => (
                                    <div key={index} className={`application-process__step`}>
                                        <a className={`application-process__link`} aria-label={step.step_link_aria_label?.value ? step.step_link_aria_label.value : 'application step'} href={step.step_link_target?.value}>
                                            <motion.div
                                                className={`application-process__step-inner`}
                                                variants={itemVariants}
                                            >
                                                <div className={`application-process__step-count`}>
                                                    {index + 1}
                                                </div>
                                                {step.step_heading?.value &&
                                                    <h3 className={`application-process__step-heading`}>
                                                        {step.step_heading.value}
                                                    </h3>
                                                }
                                            </motion.div>
                                        </a>
                                        <div className={`application-process__step-decorator`}></div>
                                    </div>
                                ))}
                            </motion.div>
                        )}
                    </>
                )}
                {text?.content?.value &&
                    <div className={`application-process__text`}>
                        {text?.content?.value && CompileMarkdown(text.content.value)}
                    </div>
                }
            </div>
        </section>
    )
}

export default ApplicationProcess